import { AxiosError } from 'axios';

/** One of the backend's SelfServeRateCard.RoleBand names. */
export type RoleBand = 'HOURLY' | 'TRADES' | 'OFFICE' | 'LEADERSHIP';

/**
 * What the backend's SelfServeSignupService.submit() accepts.
 *
 * No dollar amounts, deliberately: the server computes the rate, contract
 * value and down payment from roleBand + hires on its own copy of the rate
 * card (avrio-backend#167), because those figures become the Stripe Checkout
 * amount. A client-supplied price is exactly what that change removed.
 */
export interface SignupSubmission {
  firstName: string;
  lastName: string;
  workEmail: string;
  companyName: string;
  /**
   * Down-payment flow only. Absent on a card-on-file (Flex) signup, where the server takes no price input at all: it
   * saves a card and charges per hire at offer (billing PR (b); request shape agreed with the billing session).
   */
  roleBand?: RoleBand;
  /** Down-payment flow only; within [MIN_SELF_SERVE_HIRES, MAX_SELF_SERVE_HIRES]. Absent on a Flex signup. */
  hires?: number;
  currency: string;
  /**
   * The checkbox state at submission - the backend stamps its own receipt
   * time as the server-side proof of consent rather than trusting a
   * client-supplied timestamp, so this only needs to be a boolean.
   */
  termsAccepted: boolean;
}

export interface Context {
  isLoading: boolean;
  error?: AxiosError;
  /**
   * Set once SUBMIT succeeds - where to send the browser to pay the down
   * payment. Submitting no longer provisions a tenant directly; that waits
   * on Stripe's webhook once the payment completes. As of 2026-09-16 this is
   * returned directly by the submit call itself - there is no longer a
   * separate emailed confirmation step in between.
   */
  checkoutUrl: string | null;
}

export enum State {
  INITIAL = 'idle',
  SUBMITTING = 'SUBMITTING',
  SUBMITTED = 'SUBMITTED',
  /** Transient - the submit form's error toast, auto-resets after 200ms. */
  ERROR = 'error',
}

export enum EventType {
  SUBMIT = 'SUBMIT',
}

export type submitEvent = {
  type: EventType.SUBMIT;
  payload: SignupSubmission;
};

export type Event = submitEvent;
